/**
 * Export API utility for rendering slides to PNG/Video via backend
 */

import { getAuthHeaders, getAuthToken } from './authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Extract all CSS rules from the page's stylesheets.
 * This captures the compiled Tailwind CSS and any other styles.
 * 
 * @returns {string} All CSS rules as a single string
 */
function extractAllCss() {
  const cssRules = [];
  
  // Iterate through all stylesheets
  for (const sheet of document.styleSheets) {
    try {
      // Some stylesheets (cross-origin) may not be accessible
      const rules = sheet.cssRules || sheet.rules;
      for (const rule of rules) {
        cssRules.push(rule.cssText);
      }
    } catch (e) {
      // Skip inaccessible stylesheets (CORS)
      console.warn('Could not access stylesheet:', sheet.href);
    }
  }
  
  return cssRules.join('\n');
}

/**
 * Get the HTML and CSS needed for export.
 * 
 * Handles 3D canvas: swaps the live Three.js <canvas> with a static snapshot
 * <img> so Playwright can render it server-side (WebGL isn't available in
 * the set_content export pipeline).
 * 
 * @param {HTMLElement} element - The element to export
 * @returns {{html: string, css: string}} HTML and CSS for export
 */
export function getExportData(element) {
  const cleanups = [];

  // Pre-export: swap 3D canvas with static snapshot for server rendering
  const p3dContainer = element.querySelector('[data-primitives3d]');
  if (p3dContainer) {
    const canvas = p3dContainer.querySelector('canvas');
    if (canvas) {
      try {
        const dataUrl = canvas.toDataURL('image/png');
        const img = document.createElement('img');
        img.src = dataUrl;
        img.setAttribute('style', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;');
        canvas.style.display = 'none';
        p3dContainer.appendChild(img);
        cleanups.push(() => {
          canvas.style.display = '';
          img.remove();
        });
      } catch (e) {
        console.warn('Failed to capture 3D canvas for export:', e);
      }
    }
  }

  // Pre-export: swap generative background canvas with static snapshot
  const genBgContainer = element.querySelector('[data-generative-bg]');
  if (genBgContainer) {
    const canvas = genBgContainer.querySelector('canvas');
    if (canvas) {
      try {
        const dataUrl = canvas.toDataURL('image/png');
        const img = document.createElement('img');
        img.src = dataUrl;
        img.setAttribute('style', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;');
        canvas.style.display = 'none';
        genBgContainer.appendChild(img);
        cleanups.push(() => {
          canvas.style.display = '';
          img.remove();
        });
      } catch (e) {
        console.warn('Failed to capture generative background canvas for export:', e);
      }
    }
  }

  // Get the outer HTML of the element
  const html = element.outerHTML;
  
  // Extract all CSS from the page
  const css = extractAllCss();

  // Restore live canvases after capture
  cleanups.forEach(fn => fn());
  
  return { html, css };
}

/**
 * Export a slide to PNG using the backend Playwright renderer.
 * This ensures pixel-perfect output including CSS backdrop-filter effects.
 * 
 * @param {string} html - The HTML content to render
 * @param {string} css - CSS styles to apply
 * @param {Object} options - Export options
 * @param {number} options.width - Viewport width (default 1080)
 * @param {number} options.height - Viewport height (default 1350)
 * @param {number} options.scale - Device pixel ratio (default 2.0)
 * @returns {Promise<Blob>} PNG image as Blob
 */
export async function exportSlideToBlob(html, css = '', options = {}) {
  const { width = 1080, height = 1350, scale = 2.0 } = options;
  
  const response = await fetch(`${BACKEND_URL}/api/export/slide`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      html,
      css,
      width,
      height,
      scale,
    }),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Export failed: ${error}`);
  }
  
  return await response.blob();
}

/**
 * Export a slide to JPEG using the backend Playwright renderer.
 * Instagram-optimized: scale 1.0, quality 85%, black background.
 * 
 * @param {string} html - The HTML content to render
 * @param {string} css - CSS styles to apply
 * @param {Object} options - Export options
 * @param {number} options.width - Viewport width (default 1080)
 * @param {number} options.height - Viewport height (default 1350)
 * @param {number} options.quality - JPEG quality 0-100 (default 85)
 * @returns {Promise<Blob>} JPEG image as Blob
 */
export async function exportSlideToJpegBlob(html, css = '', options = {}) {
  const { width = 1080, height = 1350, quality = 85 } = options;
  
  const response = await fetch(`${BACKEND_URL}/api/export/slide-jpeg`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      html,
      css,
      width,
      height,
      quality,
    }),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`JPEG export failed: ${error}`);
  }
  
  return await response.blob();
}


/**
 * Phase 5: Worker-first image export (PNG or JPEG).
 * 
 * Uses the v2 worker-based rendering endpoint. The worker renders the HTML
 * to an image, uploads to S3, and returns a presigned download URL.
 * 
 * Auth token is read from localStorage (same pattern as storageApi).
 * 
 * @param {string} html - The HTML content to render
 * @param {string} css - CSS styles to apply
 * @param {Object} options - Export options
 * @param {'png'|'jpeg'} options.format - Output format (default 'jpeg')
 * @param {number} options.width - Viewport width (default 1080)
 * @param {number} options.height - Viewport height (default 1350)
 * @param {number} options.scale - Device pixel ratio (default 1.0; 2.0 for HiDPI PNG)
 * @param {number} options.quality - JPEG quality 0-100 (default 85)
 * @param {boolean} options.transparent - Transparent background, PNG only (default false)
 * @returns {Promise<Blob>} Image as Blob
 */
export async function exportSlideV2(html, css = '', options = {}) {
  const {
    format = 'jpeg',
    width = 1080,
    height = 1350,
    scale = format === 'png' ? 2.0 : 1.0,
    quality = 85,
    transparent = false,
    carouselId = null,
    slideId = null,
  } = options;

  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });

  const body = {
    html,
    css,
    width,
    height,
    format,
    scale,
    quality,
    transparent,
  };
  // PR R2: Include context IDs for render output tracking
  if (carouselId) body.carousel_id = carouselId;
  if (slideId) body.slide_id = slideId;

  const response = await fetch(`${BACKEND_URL}/api/export/v2/image`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // Read body once (no .clone() — avoids "body already used" errors)
    const errorText = await response.text();

    // Parse structured error for blank render detection
    let errorDetail = errorText;
    let errorType = 'export_failed';
    try {
      const errorJson = JSON.parse(errorText);
      const detail = errorJson.detail;
      if (typeof detail === 'object' && detail?.error === 'blank_render_detected') {
        errorType = 'blank_render_detected';
        errorDetail = detail.message || 'Rendered image was blank or empty.';
      } else if (typeof detail === 'string') {
        errorDetail = detail;
        if (detail.includes('blank_render_detected')) {
          errorType = 'blank_render_detected';
        }
      }
    } catch { /* not JSON */ }

    const error = new Error(
      errorType === 'blank_render_detected'
        ? `Export produced a blank image. ${errorDetail}`
        : `V2 image export failed: ${errorDetail}`
    );
    error.status = response.status;
    error.errorType = errorType;
    throw error;
  }

  const result = await response.json();

  // Log cache metadata for debugging (visible in browser Network tab and console)
  const cacheInfo = {
    cached: result.cached || false,
    execution: result.execution || 'unknown',
    job_id: result.job_id || null,
    source_job_id: result.source_job_id || null,
    render_hash: result.render_hash || null,
    format: result.format,
    size_bytes: result.size_bytes,
  };
  console.log(
    `[Export] ${format.toUpperCase()} export ready: cached=${cacheInfo.cached}, execution=${cacheInfo.execution}` +
    (cacheInfo.source_job_id ? `, source_job=${cacheInfo.source_job_id}` : '')
  );

  // Download the image from the presigned S3 URL
  const imageResponse = await fetch(result.download_url);
  if (!imageResponse.ok) {
    throw new Error(`Failed to download exported image from S3 (${imageResponse.status})`);
  }

  return await imageResponse.blob();
}

/**
 * Export a slide to PNG and trigger download.
 * 
 * @param {string} html - The HTML content to render
 * @param {string} css - CSS styles to apply
 * @param {string} filename - Download filename
 * @param {Object} options - Export options
 * @returns {Promise<string>} Object URL of the exported image
 */
export async function exportSlideAndDownload(html, css = '', filename = 'slide.png', options = {}) {
  const blob = await exportSlideToBlob(html, css, options);
  const objectUrl = URL.createObjectURL(blob);
  
  // Trigger download
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return objectUrl;
}

/**
 * Export a slide to MP4 video using the backend Playwright recorder.
 * Optimized for Instagram carousel uploads (1080x1350, H.264, 3500kbps).
 * 
 * @param {string} html - The HTML content to render (with video widget)
 * @param {string} css - CSS styles to apply
 * @param {number} duration - Video duration in seconds
 * @param {Object} options - Export options
 * @param {number} options.width - Viewport width (default 1080)
 * @param {number} options.height - Viewport height (default 1350)
 * @returns {Promise<Blob>} MP4 video as Blob
 */
export async function exportSlideToVideoBlob(html, css = '', duration, options = {}) {
  const { width = 1080, height = 1350 } = options;
  
  const response = await fetch(`${BACKEND_URL}/api/export/slide-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      html,
      css,
      duration,
      width,
      height,
    }),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Video export failed: ${error}`);
  }
  
  return await response.blob();
}

/**
 * Video export progress stages (5-step model)
 * 
 * User-facing steps:
 *   Step 1: BACKGROUND             - "Creating background..."
 *   Step 2: PREPARING_VIDEO        - "Preparing video: X.Xs of Y.Ys" (proxy generation)
 *   Step 3: PREPARING_COMPOSITING  - "Preparing for compositing..." (FFmpeg warm-up)
 *   Step 4: COMPOSITING            - "Compositing: X.Xs of Y.Ys"
 *   Step 5: DOWNLOADING            - Download progress bar
 */
export const VideoExportStage = {
  PREPARING: 'preparing',                       // Initial state
  BACKGROUND: 'background',                     // Step 1: Creating background
  PREPARING_VIDEO: 'preparing_video',           // Step 2: Proxy or placeholder
  PREPARING_COMPOSITING: 'preparing_compositing', // Step 3: FFmpeg warm-up
  COMPOSITING: 'compositing',                   // Step 4: FFmpeg compositing
  FINALIZING: 'finalizing',                     // Step 5: Download
  DOWNLOADING: 'downloading',                   // Streaming download (same as finalizing)
  COMPLETE: 'complete',
  ERROR: 'error',
};

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Start video export with progress tracking.
 * Uses async background processing to avoid HTTP timeouts.
 * 
 * Flow:
 * 1. POST /api/export/slide-video-with-progress -> returns job_id immediately
 * 2. Poll /api/export/video-progress/{job_id} for real progress
 * 3. GET /api/export/video-result/{job_id} when complete
 * 
 * @param {string} html - The HTML content to render
 * @param {string} css - CSS styles to apply
 * @param {number} duration - Video duration in seconds
 * @param {Object} options - Export options
 * @returns {Promise<Blob>}
 */
export function startVideoExportWithProgress(html, css = '', duration, options = {}) {
  const { width = 1080, height = 1350, onProgress, forceLegacy = false } = options;
  
  return new Promise(async (resolve, reject) => {
    let pollInterval = null;
    let jobId = null;
    
    try {
      // Initial progress update
      if (onProgress) {
        onProgress({
          stage: VideoExportStage.PREPARING,
          progress_percent: 5,
        });
      }
      
      // Step 1: Start the async export job
      const headers = {
        'Content-Type': 'application/json',
      };
      // Phase 3: Signal backend that legacy is the only option (no asset_id)
      if (forceLegacy) {
        headers['X-Force-Legacy'] = 'true';
      }
      
      const startResponse = await fetch(`${BACKEND_URL}/api/export/slide-video-with-progress`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          html,
          css,
          duration,
          width,
          height,
        }),
      });
      
      if (!startResponse.ok) {
        let errorText;
        try {
          errorText = await startResponse.clone().text();
        } catch {
          errorText = `HTTP ${startResponse.status} ${startResponse.statusText}`;
        }
        let detail = errorText;
        try {
          const json = JSON.parse(errorText);
          detail = (typeof json.detail === 'object') ? (json.detail.message || JSON.stringify(json.detail)) : (json.detail || errorText);
        } catch { /* keep raw */ }

        const err = new Error(detail);
        err.status = startResponse.status;

        // Phase 3: 410 means legacy export is gated off
        if (startResponse.status === 410) {
          err.errorType = 'legacy_gated';
          err.message = 'In-process video rendering is disabled. Please re-upload the video to use worker-based export.';
        }

        throw err;
      }
      
      const { job_id } = await startResponse.json();
      jobId = job_id;
      
      // Step 2: Poll for progress
      const pollProgress = async () => {
        try {
          const progressResponse = await fetch(`${BACKEND_URL}/api/export/video-progress/${jobId}`);
          
          if (!progressResponse.ok) {
            // Job might not exist anymore
            if (progressResponse.status === 404) {
              clearInterval(pollInterval);
              reject(new Error('Export job not found'));
            }
            return;
          }
          
          const progress = await progressResponse.json();
          
          // Debug: Log raw backend response for key stages
          if (progress.stage === 'preparing_video' || progress.stage === 'compositing') {
            console.log('[VideoExport] Backend progress:', {
              stage: progress.stage,
              step_number: progress.step_number,
              encoded_time: progress.encoded_time,
              total_duration: progress.total_duration,
              proxy_needed: progress.proxy_needed,
              progress_percent: progress.progress_percent,
            });
          }
          
          // Map backend stage names to frontend stages (5-step model)
          let stage = progress.stage;
          if (stage === 'background') stage = VideoExportStage.BACKGROUND;
          else if (stage === 'preparing_video') stage = VideoExportStage.PREPARING_VIDEO;
          else if (stage === 'preparing_compositing') stage = VideoExportStage.PREPARING_COMPOSITING;
          else if (stage === 'compositing') stage = VideoExportStage.COMPOSITING;
          else if (stage === 'finalizing') stage = VideoExportStage.FINALIZING;
          else if (stage === 'complete') stage = VideoExportStage.COMPLETE;
          else if (stage === 'error') stage = VideoExportStage.ERROR;
          else if (stage === 'preparing') stage = VideoExportStage.PREPARING;
          
          // Update progress callback
          if (onProgress) {
            onProgress({
              stage,
              stage_message: progress.stage_message,
              progress_percent: progress.progress_percent || 0,
              encoded_time: progress.encoded_time,
              total_duration: progress.total_duration || duration,
              encoding_speed: progress.encoding_speed,
              error_message: progress.error_message,
              // 5-step model fields
              step_number: progress.step_number,
              total_steps: progress.total_steps,
              proxy_needed: progress.proxy_needed,
              proxy_time: progress.proxy_time,
              proxy_duration: progress.proxy_duration,
            });
          }
          
          // Check for completion or error
          if (progress.stage === 'complete') {
            clearInterval(pollInterval);
            pollInterval = null;
            
            // Step 3: Download the result
            if (onProgress) {
              onProgress({
                stage: VideoExportStage.DOWNLOADING,
                progress_percent: 90,
                download_percent: 0,
              });
            }
            
            const resultResponse = await fetch(`${BACKEND_URL}/api/export/video-result/${jobId}`);
            
            if (!resultResponse.ok) {
              const error = await resultResponse.text();
              throw new Error(`Failed to download result: ${error}`);
            }
            
            // Stream the download with progress
            const contentLength = resultResponse.headers.get('Content-Length');
            const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
            
            if (!totalBytes || !resultResponse.body) {
              // Fall back to simple blob download
              const blob = await resultResponse.blob();
              if (onProgress) {
                onProgress({
                  stage: VideoExportStage.COMPLETE,
                  progress_percent: 100,
                });
              }
              resolve(blob);
              return;
            }
            
            // Stream with progress tracking
            const reader = resultResponse.body.getReader();
            const chunks = [];
            let receivedBytes = 0;
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              chunks.push(value);
              receivedBytes += value.length;
              
              const downloadPercent = Math.round((receivedBytes / totalBytes) * 100);
              
              if (onProgress) {
                onProgress({
                  stage: VideoExportStage.DOWNLOADING,
                  progress_percent: 90 + Math.floor(downloadPercent * 0.1),
                  download_percent: downloadPercent,
                  downloaded_bytes: receivedBytes,
                  total_bytes: totalBytes,
                  downloaded_formatted: formatBytes(receivedBytes),
                  total_bytes_formatted: formatBytes(totalBytes),
                });
              }
            }
            
            // Combine chunks into blob
            const allChunks = new Uint8Array(receivedBytes);
            let position = 0;
            for (const chunk of chunks) {
              allChunks.set(chunk, position);
              position += chunk.length;
            }
            
            const blob = new Blob([allChunks], { type: 'video/mp4' });
            
            if (onProgress) {
              onProgress({
                stage: VideoExportStage.COMPLETE,
                progress_percent: 100,
                download_percent: 100,
              });
            }
            
            resolve(blob);
            
          } else if (progress.stage === 'error') {
            clearInterval(pollInterval);
            pollInterval = null;
            
            if (onProgress) {
              onProgress({
                stage: VideoExportStage.ERROR,
                progress_percent: 0,
                error_message: progress.error_message,
              });
            }
            
            reject(new Error(progress.error_message || 'Export failed'));
          }
          
        } catch (pollError) {
          console.error('Progress poll error:', pollError);
          // Don't reject on poll errors, let it retry
        }
      };
      
      // Start polling every 2 seconds (reduced from 500ms - progress updates are slow anyway)
      pollInterval = setInterval(pollProgress, 2000);
      
      // Also poll immediately
      await pollProgress();
      
    } catch (error) {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (onProgress) {
        onProgress({
          stage: VideoExportStage.ERROR,
          progress_percent: 0,
          error_message: error.message,
        });
      }
      reject(error);
    }
  });
}


// ══════════════════════════════════════════════════════════════════════════
// V2 EXPORT — dispatch-based rendering via media workers (Phase 5)
//
// Used when the video has an asset_id (uploaded via S3 presigned flow).
// Falls back to legacy startVideoExportWithProgress when no asset_id.
// ══════════════════════════════════════════════════════════════════════════

/**
 * Compute the video element's bounding rect relative to the slide canvas.
 * Finds the <video> element inside the export node and returns its position.
 *
 * @param {HTMLElement} exportNode - The slide canvas DOM element
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export function computeVideoRect(exportNode) {
  if (!exportNode) return null;

  const video = exportNode.querySelector('video');
  if (!video) return null;

  const canvasRect = exportNode.getBoundingClientRect();
  const videoRect = video.getBoundingClientRect();

  // The slide canvas is 1080×1350 but rendered at a smaller scale in the preview.
  // We need the ratio to convert screen pixels → canvas pixels.
  const scaleX = 1080 / canvasRect.width;
  const scaleY = 1350 / canvasRect.height;

  return {
    x: Math.round((videoRect.left - canvasRect.left) * scaleX),
    y: Math.round((videoRect.top - canvasRect.top) * scaleY),
    width: Math.round(videoRect.width * scaleX),
    height: Math.round(videoRect.height * scaleY),
  };
}

/**
 * Start a v2 video export job via media workers.
 *
 * Flow:
 *   1. POST /api/export/v2/video → { job_id, status }
 *   2. Poll GET /api/export/v2/progress/{job_id} for progress
 *   3. GET /api/export/v2/result/{job_id} → { download_url }
 *   4. Fetch blob from download_url
 *
 * @param {string} assetId - Confirmed media asset ID (source video in S3)
 * @param {string} html - Slide HTML
 * @param {string} css - Slide CSS
 * @param {{ x: number, y: number, width: number, height: number }} videoRect
 * @param {Object} options
 * @param {function} options.onProgress - Progress callback
 * @param {boolean} [options.roundedCorners=false]
 * @param {number} [options.canvasWidth=1080]
 * @param {number} [options.canvasHeight=1350]
 * @returns {Promise<Blob>} Rendered video as blob
 */
export function startVideoExportV2(assetId, html, css, videoRect, options = {}) {
  const {
    onProgress,
    roundedCorners = false,
    canvasWidth = 1080,
    canvasHeight = 1350,
    carouselId = null,
    slideId = null,
  } = options;

  return new Promise(async (resolve, reject) => {
    let pollInterval = null;
    let jobId = null;

    try {
      // Initial progress
      if (onProgress) {
        onProgress({
          stage: VideoExportStage.PREPARING,
          progress_percent: 5,
          stage_message: 'Dispatching to render worker...',
        });
      }

      // Step 1: Start the render job
      const startResponse = await fetch(`${BACKEND_URL}/api/export/v2/video`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          asset_id: assetId,
          slide_html: html,
          slide_css: css,
          video_rect: videoRect,
          rounded_corners: roundedCorners,
          canvas_width: canvasWidth,
          canvas_height: canvasHeight,
          ...(carouselId && { carousel_id: carouselId }),
          ...(slideId && { slide_id: slideId }),
        }),
      });

      if (!startResponse.ok) {
        let errorText;
        try {
          errorText = await startResponse.clone().text();
        } catch {
          errorText = `HTTP ${startResponse.status} ${startResponse.statusText}`;
        }
        let detail = errorText;
        let errorType = 'unknown';
        let useLegacy = false;
        let mode = '';

        try {
          const json = JSON.parse(errorText);
          const d = json.detail;

          if (typeof d === 'object' && d !== null) {
            // Structured error response
            detail = d.message || JSON.stringify(d);
            mode = d.mode || '';
            useLegacy = d.use_legacy === true;
          } else {
            detail = d || errorText;
          }
        } catch { /* keep raw */ }

        // Create typed error with metadata
        if (startResponse.status === 409) {
          errorType = 'emergency_mode';
        } else if (startResponse.status === 503) {
          errorType = 'workers_unavailable';
        } else if (startResponse.status === 400) {
          errorType = 'invalid_asset';
        }

        const err = new Error(detail);
        err.status = startResponse.status;
        err.errorType = errorType;
        err.useLegacy = useLegacy;
        err.mode = mode;
        throw err;
      }

      const startData = await startResponse.json();
      const { job_id } = startData;
      jobId = job_id;

      // Log cache metadata for video exports
      if (startData.cached) {
        console.log(
          `[Export] MP4 export ready: cached=${startData.cached}, execution=cached` +
          (startData.source_job_id ? `, source_job=${startData.source_job_id}` : '')
        );
      }

      if (onProgress) {
        onProgress({
          stage: VideoExportStage.PREPARING,
          progress_percent: 10,
          stage_message: 'Render job dispatched to worker...',
        });
      }

      // Step 2: Poll for progress
      const pollProgress = async () => {
        try {
          const progressResponse = await fetch(
            `${BACKEND_URL}/api/export/v2/progress/${jobId}`,
            { headers: await getAuthHeaders() }
          );

          if (!progressResponse.ok) {
            if (progressResponse.status === 404) {
              clearInterval(pollInterval);
              reject(new Error('Render job not found'));
            }
            return;
          }

          const job = await progressResponse.json();

          // Map job status/stage to VideoExportStage
          let stage = VideoExportStage.PREPARING;
          if (job.status === 'completed') stage = VideoExportStage.COMPLETE;
          else if (job.status === 'failed') stage = VideoExportStage.ERROR;
          else if (job.stage === 'downloading_source') stage = VideoExportStage.PREPARING;
          else if (job.stage === 'background' || job.stage === 'creating_background') stage = VideoExportStage.BACKGROUND;
          else if (job.stage === 'preparing_video') stage = VideoExportStage.PREPARING_VIDEO;
          else if (job.stage === 'preparing_compositing') stage = VideoExportStage.PREPARING_COMPOSITING;
          else if (job.stage === 'compositing') stage = VideoExportStage.COMPOSITING;
          else if (job.stage === 'finalizing' || job.stage === 'uploading_result') stage = VideoExportStage.FINALIZING;

          if (onProgress) {
            // Session 34: Merge rich progress_data fields from worker SSE
            const pd = job.progress_data || {};
            onProgress({
              stage,
              progress_percent: job.progress_percent || 0,
              stage_message: pd.stage_message || job.stage || '',
              step_number: pd.step || job.step_number,
              total_steps: pd.total_steps || 5,
              error_message: job.error_message,
              // Compositing progress (step 4)
              encoded_time: pd.encoded_time,
              total_duration: pd.total_duration,
              encoding_speed: pd.encoding_speed,
              // Proxy progress (step 2)
              proxy_needed: pd.proxy_needed,
              proxy_time: pd.proxy_time,
              proxy_duration: pd.proxy_duration,
            });
          }

          // Completion
          if (job.status === 'completed') {
            clearInterval(pollInterval);
            pollInterval = null;

            if (onProgress) {
              onProgress({
                stage: VideoExportStage.DOWNLOADING,
                progress_percent: 90,
                download_percent: 0,
                stage_message: 'Downloading result...',
              });
            }

            // Step 3: Get presigned download URL
            const resultResponse = await fetch(
              `${BACKEND_URL}/api/export/v2/result/${jobId}`,
              { headers: await getAuthHeaders() }
            );

            if (!resultResponse.ok) {
              const errText = await resultResponse.text();
              throw new Error(`Failed to get render result: ${errText}`);
            }

            const { download_url } = await resultResponse.json();

            // Step 4: Fetch blob from presigned URL
            const blobResponse = await fetch(download_url);
            if (!blobResponse.ok) {
              throw new Error(`Failed to download rendered video (${blobResponse.status})`);
            }

            // Stream download with progress
            const contentLength = blobResponse.headers.get('Content-Length');
            const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;

            if (!totalBytes || !blobResponse.body) {
              const blob = await blobResponse.blob();
              if (onProgress) {
                onProgress({
                  stage: VideoExportStage.COMPLETE,
                  progress_percent: 100,
                });
              }
              resolve(blob);
              return;
            }

            // Stream with progress tracking
            const reader = blobResponse.body.getReader();
            const chunks = [];
            let receivedBytes = 0;

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              chunks.push(value);
              receivedBytes += value.length;

              const downloadPercent = Math.round((receivedBytes / totalBytes) * 100);

              if (onProgress) {
                onProgress({
                  stage: VideoExportStage.DOWNLOADING,
                  progress_percent: 90 + Math.floor(downloadPercent * 0.1),
                  download_percent: downloadPercent,
                  downloaded_bytes: receivedBytes,
                  total_bytes: totalBytes,
                  downloaded_formatted: formatBytes(receivedBytes),
                  total_bytes_formatted: formatBytes(totalBytes),
                });
              }
            }

            const allChunks = new Uint8Array(receivedBytes);
            let position = 0;
            for (const chunk of chunks) {
              allChunks.set(chunk, position);
              position += chunk.length;
            }

            const blob = new Blob([allChunks], { type: 'video/mp4' });

            if (onProgress) {
              onProgress({
                stage: VideoExportStage.COMPLETE,
                progress_percent: 100,
                download_percent: 100,
              });
            }

            resolve(blob);

          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            pollInterval = null;

            if (onProgress) {
              onProgress({
                stage: VideoExportStage.ERROR,
                progress_percent: 0,
                error_message: job.error_message,
              });
            }

            reject(new Error(job.error_message || 'Render failed'));
          }

        } catch (pollError) {
          console.error('[V2 Export] Progress poll error:', pollError);
          // Don't reject on poll errors — let it retry
        }
      };

      // Poll every 2 seconds
      pollInterval = setInterval(pollProgress, 2000);
      await pollProgress(); // Immediate first poll

    } catch (error) {
      if (pollInterval) clearInterval(pollInterval);
      if (onProgress) {
        onProgress({
          stage: VideoExportStage.ERROR,
          progress_percent: 0,
          error_message: error.message,
        });
      }
      reject(error);
    }
  });
}
